import { useState, useMemo } from 'react';
import { RefreshCw, MapPin, Users, CheckCircle, XCircle, Filter, Truck } from 'lucide-react';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Input,
  Label,
} from '@flote/ui';
import { RouteMap, RouteLegend } from '../../components/map';
import { deliveryTsr } from '../../api';
import type { DeliveryTaskStatus } from '@flote/contracts/http';

function StatsCard({
  title,
  value,
  icon: Icon,
  color,
  isLoading,
}: {
  title: string;
  value: number;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
  isLoading?: boolean;
}) {
  return (
    <Card>
      <CardContent className="flex items-center gap-4 p-4">
        <div className={`rounded-full p-2 ${color}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <p className="text-sm text-muted-foreground">{title}</p>
          {isLoading ? (
            <div className="h-8 w-8 animate-pulse rounded bg-muted" />
          ) : (
            <p className="text-2xl font-bold">{value}</p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

/** Empty state shown in the legend panel when no deliveries exist */
function EmptyLegend({ hasFiltersApplied }: { hasFiltersApplied: boolean }) {
  return (
    <Card className="h-full">
      <CardContent className="flex flex-col items-center justify-center h-full py-8 text-center">
        <div className="rounded-full bg-muted p-4 mb-4">
          <Truck className="h-8 w-8 text-muted-foreground" />
        </div>
        {hasFiltersApplied ? (
          <>
            <h3 className="text-base font-medium">No Deliveries Found</h3>
            <p className="text-sm text-muted-foreground mt-1 max-w-[240px]">
              No deliveries match the current filters. Try adjusting the date range or other filters.
            </p>
          </>
        ) : (
          <>
            <h3 className="text-base font-medium">No Delivery History</h3>
            <p className="text-sm text-muted-foreground mt-1 max-w-[240px]">
              Delivery routes will appear here once drivers complete deliveries.
            </p>
          </>
        )}
      </CardContent>
    </Card>
  );
}

export function DeliveriesPage() {
  const [selectedRouteId, setSelectedRouteId] = useState<string | null>(null);
  const [hiddenRouteIds, setHiddenRouteIds] = useState<Set<string>>(new Set());
  const [filters, setFilters] = useState({
    driverId: '',
    teamId: '',
    status: '' as DeliveryTaskStatus | '',
    fromDate: '',
    toDate: '',
  });

  // Build query parameters - no date defaults, fetch all time by default
  const queryParams = useMemo(() => ({
    status: filters.status || undefined,
    driverId: filters.driverId || undefined,
    teamId: filters.teamId || undefined,
    fromDate: filters.fromDate || undefined,
    toDate: filters.toDate || undefined,
    simplify: true,
    simplifyTolerance: 0.0001,
  }), [filters]);

  // Fetch routes
  const {
    data: routesResponse,
    isLoading: routesLoading,
    refetch,
    isFetching,
  } = deliveryTsr.adminRoutes.getBatchRoutes.useQuery({
    queryKey: ['routes', queryParams],
    queryData: { query: queryParams },
  });

  const routesData = routesResponse?.status === 200 ? routesResponse.body : null;
  const routes = routesData?.routes ?? [];

  // Fetch teams for filter
  const { data: teamsResponse } = deliveryTsr.adminTeams.list.useQuery({
    queryKey: ['teams-list'],
  });
  const teams = teamsResponse?.status === 200 ? teamsResponse.body : [];

  // Fetch drivers for filter
  const { data: driversResponse } = deliveryTsr.adminDrivers.list.useQuery({
    queryKey: ['drivers-list'],
    queryData: { query: {} },
  });
  const drivers = driversResponse?.status === 200 ? driversResponse.body : [];

  // Toggle route visibility
  const handleToggleVisibility = (taskId: string) => {
    setHiddenRouteIds(prev => {
      const next = new Set(prev);
      if (next.has(taskId)) {
        next.delete(taskId);
      } else {
        next.add(taskId);
      }
      return next;
    });
  };

  // Compute stats
  const stats = useMemo(() => {
    const uniqueDrivers = new Set(routes.map(r => r.driverId)).size;
    const completed = routes.filter(r => r.status === 'completed').length;
    const failed = routes.filter(r => r.status === 'failed').length;
    return {
      totalRoutes: routes.length,
      uniqueDrivers,
      completed,
      failed,
    };
  }, [routes]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Delivery History</h1>
          <p className="text-muted-foreground">
            View historical GPS routes for past deliveries
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => refetch()}
          disabled={isFetching}
          className="w-full sm:w-auto"
        >
          <RefreshCw className={`mr-2 h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatsCard
          title="Total Routes"
          value={stats.totalRoutes}
          icon={MapPin}
          color="bg-blue-100 text-blue-600"
          isLoading={routesLoading}
        />
        <StatsCard
          title="Unique Drivers"
          value={stats.uniqueDrivers}
          icon={Users}
          color="bg-purple-100 text-purple-600"
          isLoading={routesLoading}
        />
        <StatsCard
          title="Completed"
          value={stats.completed}
          icon={CheckCircle}
          color="bg-green-100 text-green-600"
          isLoading={routesLoading}
        />
        <StatsCard
          title="Failed"
          value={stats.failed}
          icon={XCircle}
          color="bg-red-100 text-red-600"
          isLoading={routesLoading}
        />
      </div>

      {/* Filters */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Filter className="h-4 w-4" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
            {/* Date From */}
            <div className="space-y-1.5">
              <Label htmlFor="fromDate">From Date</Label>
              <Input
                id="fromDate"
                type="date"
                value={filters.fromDate}
                placeholder="All time"
                onChange={(e) => setFilters(f => ({ ...f, fromDate: e.target.value }))}
              />
            </div>

            {/* Date To */}
            <div className="space-y-1.5">
              <Label htmlFor="toDate">To Date</Label>
              <Input
                id="toDate"
                type="date"
                value={filters.toDate}
                placeholder="All time"
                onChange={(e) => setFilters(f => ({ ...f, toDate: e.target.value }))}
              />
            </div>

            {/* Team */}
            <div className="space-y-1.5">
              <Label>Team</Label>
              <Select
                value={filters.teamId}
                onValueChange={(value) => setFilters(f => ({ ...f, teamId: value === 'all' ? '' : value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All Teams" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Teams</SelectItem>
                  {teams.map((team) => (
                    <SelectItem key={team.id} value={team.id}>
                      {team.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Driver */}
            <div className="space-y-1.5">
              <Label>Driver</Label>
              <Select
                value={filters.driverId}
                onValueChange={(value) => setFilters(f => ({ ...f, driverId: value === 'all' ? '' : value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All Drivers" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Drivers</SelectItem>
                  {drivers.map((driver) => (
                    <SelectItem key={driver.id} value={driver.id}>
                      {driver.displayName || driver.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Status */}
            <div className="space-y-1.5">
              <Label>Status</Label>
              <Select
                value={filters.status}
                onValueChange={(value) => setFilters(f => ({
                  ...f,
                  status: value === 'all' ? '' : value as DeliveryTaskStatus,
                }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                  <SelectItem value="in_transit">In Transit</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Map + Legend Layout - always rendered for visual context */}
      <div className="flex flex-col lg:flex-row gap-4">
        {/* Map - takes remaining space */}
        <Card className="flex-1 min-w-0 overflow-hidden">
          <CardContent className="p-0">
            <RouteMap
              data={routesData}
              selectedRouteId={selectedRouteId}
              onRouteSelect={setSelectedRouteId}
              isLoading={routesLoading}
              className="h-[500px] lg:h-[600px]"
              hiddenRouteIds={hiddenRouteIds}
            />
          </CardContent>
        </Card>

        {/* Legend / Empty State - fixed width on desktop */}
        <div className="h-[400px] lg:h-[600px] lg:w-[340px] lg:flex-shrink-0">
          {routes.length > 0 ? (
            <RouteLegend
              routes={routes}
              selectedRouteId={selectedRouteId}
              onRouteSelect={setSelectedRouteId}
              hiddenRouteIds={hiddenRouteIds}
              onToggleVisibility={handleToggleVisibility}
            />
          ) : (
            <EmptyLegend
              hasFiltersApplied={!!(filters.fromDate || filters.toDate || filters.driverId || filters.teamId || filters.status)}
            />
          )}
        </div>
      </div>
    </div>
  );
}
